import React from "react";

import * as Cmp from "@impero-com/common/utils/comparison";
import { getParentElement } from "@impero-com/common/utils/dom";
import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { Table } from "antd";
import { CheckboxProps } from "antd/es/checkbox";
import TreeSelect from "antd/es/tree-select";
import memoizeOne from "memoize-one";

import { BaseFormDisplay } from "components/form/legacyFields/formDisplay";
import { Guid } from "utils/guid";

import { Tag, TagCategory, ActivableTag } from "../types";

import * as theme from "./theme.css";

type TagsSelectorProps =
    | ({ type: "CREATE" } & WrappedWriteProps)
    | ({ type: "EDIT" } & WrappedWriteProps)
    | ({ type: "READ_ONLY" } & WrappedReadProps);

type Wrapped<T> = T & { label: string; helpText?: string };

interface TagsSelectorValues {
    tags: Array<Set<Guid>>;
    absentTags?: Set<Guid>;
}

interface ReadProps {
    values?: TagsSelectorValues;
    allTagCategories: Array<TagCategory>;
    allTags: Array<ActivableTag>;
}

type WrappedReadProps = Wrapped<ReadProps>;

interface WriteProps extends ReadProps {
    onAddTag: (tagId: Guid) => void;
    onRemoveTag: (tagId: Guid) => void;
}

type WrappedWriteProps = Wrapped<WriteProps>;

class TagsSelector extends React.PureComponent<TagsSelectorProps> {
    render = () => {
        const { label, helpText } = this.props;
        return (
            <BaseFormDisplay label={label} helpText={helpText}>
                {this.getComponent()}
            </BaseFormDisplay>
        );
    };

    getComponent = (): React.ReactNode => {
        switch (this.props.type) {
            case "READ_ONLY": {
                const { label, helpText, values, allTagCategories, allTags } =
                    this.props;
                return (
                    <WrappedReadOnlyTagsSelector
                        label={label}
                        helpText={helpText}
                        values={values}
                        allTagCategories={allTagCategories}
                        allTags={allTags}
                    />
                );
            }
            case "EDIT": {
                const {
                    label,
                    helpText,
                    values,
                    allTagCategories,
                    allTags,
                    onAddTag,
                    onRemoveTag,
                } = this.props;
                return (
                    <WrappedEditTagsSelector
                        label={label}
                        helpText={helpText}
                        values={values}
                        allTagCategories={allTagCategories}
                        allTags={allTags}
                        onAddTag={onAddTag}
                        onRemoveTag={onRemoveTag}
                    />
                );
            }
            case "CREATE": {
                const {
                    label,
                    helpText,
                    values,
                    allTagCategories,
                    allTags,
                    onAddTag,
                    onRemoveTag,
                } = this.props;
                return (
                    <WrappedCreateTagsSelector
                        label={label}
                        helpText={helpText}
                        values={values}
                        allTagCategories={allTagCategories}
                        allTags={allTags}
                        onAddTag={onAddTag}
                        onRemoveTag={onRemoveTag}
                    />
                );
            }
            default:
                return assertNever(this.props);
        }
    };
}

interface TagRow {
    category: TagCategory;
    tag: ActivableTag;
    state: "unchecked" | "checked" | "indeterminate";
}

type RawTagsSelectorProps =
    | ({
          type: "EDIT";
          onAddTag: (tagId: Guid) => void;
          onRemoveTag: (tagId: Guid) => void;
      } & ReadProps)
    | ({
          type: "READ_ONLY";
      } & ReadProps);

class RawTagsSelector extends React.PureComponent<RawTagsSelectorProps> {
    private getTagData = memoizeOne(
        (
            allTagCategories: Array<TagCategory>,
            allTags: Array<ActivableTag>
        ): [
            Record<Guid, TagCategory>,
            Record<Guid, ActivableTag>,
            Array<ActivableTag>,
        ] => {
            const categoryMap: Record<Guid, TagCategory> = {};
            for (const category of allTagCategories) {
                categoryMap[category.id] = category;
            }

            const tagMap: Record<Guid, ActivableTag> = {};
            for (const tag of allTags) {
                tagMap[tag.id] = tag;
            }

            const stringCompare = Cmp.string();
            const sortedTags = allTags.toSorted(
                Cmp.chain([
                    Cmp.map(
                        (tag) => categoryMap[tag.tagCategoryId].name,
                        stringCompare
                    ),
                    Cmp.map((tag) => tag.name, stringCompare),
                ])
            );

            return [categoryMap, tagMap, sortedTags];
        }
    );

    private getTagCounters = memoizeOne(
        (
            tags: Array<Set<Guid>>,
            absentTags: Set<Guid> | undefined
        ): Record<Guid, number> => {
            const counters: Record<Guid, number> = {};
            if (absentTags !== undefined) {
                for (const tagId of absentTags) {
                    counters[tagId] = 0;
                }
            }
            for (const tagIds of tags) {
                for (const tagId of tagIds) {
                    const counter = counters[tagId];
                    if (counter !== undefined) {
                        counters[tagId]++;
                    } else {
                        counters[tagId] = 1;
                    }
                }
            }

            return counters;
        }
    );

    private getTableDataSourceAndSelection = memoizeOne(
        (
            categoryMap: Record<Guid, TagCategory>,
            sortedTags: Array<ActivableTag>,
            tags: Array<Set<Guid>>,
            counters: Record<Guid, number>
        ): [Array<TagRow>, Array<Guid>] => {
            const dataSource: Array<TagRow> = [];
            const selectedRowKeys = [];
            for (const tag of sortedTags) {
                const counter = counters[tag.id];
                if (counter === tags.length) {
                    dataSource.push({
                        category: categoryMap[tag.tagCategoryId],
                        tag,
                        state: "checked",
                    });
                    selectedRowKeys.push(tag.id);
                } else if (counter > 0) {
                    dataSource.push({
                        category: categoryMap[tag.tagCategoryId],
                        tag,
                        state: "indeterminate",
                    });
                } else if (counter === 0) {
                    dataSource.push({
                        category: categoryMap[tag.tagCategoryId],
                        tag,
                        state: "unchecked",
                    });
                }
            }

            return [dataSource, selectedRowKeys];
        }
    );

    render = () => {
        const { values = { tags: [] }, allTagCategories, allTags } = this.props;
        const { tags, absentTags } = values;
        const [categoryMap, tagMap, sortedTags] = this.getTagData(
            allTagCategories,
            allTags
        );
        const counters = this.getTagCounters(tags, absentTags);
        const [dataSource, selectedRowKeys] =
            this.getTableDataSourceAndSelection(
                categoryMap,
                sortedTags,
                tags,
                counters
            );

        let rowSelection;
        let footer;
        const getCheckboxProps = (row: TagRow): Partial<CheckboxProps> => {
            switch (row.state) {
                case "unchecked":
                case "checked":
                    return {};
                case "indeterminate":
                    return { indeterminate: true };
                default:
                    return assertNever(row.state);
            }
        };
        switch (this.props.type) {
            case "EDIT":
                rowSelection = {
                    type: "checkbox" as const,
                    selectedRowKeys,
                    getCheckboxProps,
                    onSelect: (row: TagRow): void => {
                        if (this.props.type !== "EDIT") {
                            throw new Error(
                                "expected type EDIT, got " + this.props.type
                            );
                        }
                        switch (row.state) {
                            case "unchecked":
                                this.props.onAddTag(row.tag.id);
                                break;
                            case "checked":
                                this.props.onRemoveTag(row.tag.id);
                                break;
                            case "indeterminate":
                                this.props.onAddTag(row.tag.id);
                                break;
                            default:
                                return assertNever(row.state);
                        }
                    },
                };
                const tree = sortedTags.reduce(
                    (cats, tag) => {
                        // Early return for tags already in the table (unselected tags inclusive)
                        if (counters[tag.id] !== undefined) {
                            return cats;
                        }
                        // Do not allow to add tags that were marked as inactive
                        if (!tag.active) {
                            return cats;
                        }
                        const lastCat =
                            cats.length > 0 ? cats[cats.length - 1] : undefined;
                        if (
                            lastCat &&
                            lastCat.category.id === tag.tagCategoryId
                        ) {
                            lastCat.tags.push(tag);
                        } else {
                            cats.push({
                                category: categoryMap[tag.tagCategoryId],
                                tags: [tag],
                            });
                        }
                        return cats;
                    },
                    [] as Array<{ category: TagCategory; tags: Array<Tag> }>
                );
                footer = () => (
                    <TreeSelect
                        className={theme.tagSelect}
                        placeholder={t("TagsSelector_AddTag")}
                        showSearch={true}
                        showArrow={false}
                        dropdownStyle={{
                            maxHeight: 200,
                        }}
                        getPopupContainer={getParentElement}
                        filterTreeNode={(inputValue, treeNode) => {
                            const id = treeNode.props.value;
                            const tag = tagMap[id];
                            if (tag) {
                                if (
                                    tag.name
                                        .toLowerCase()
                                        .indexOf(inputValue.toLowerCase()) >= 0
                                ) {
                                    return true;
                                }
                                const cat = categoryMap[tag.tagCategoryId];
                                if (
                                    cat &&
                                    cat.name
                                        .toLowerCase()
                                        .indexOf(inputValue.toLowerCase()) >= 0
                                ) {
                                    return true;
                                }
                                return false;
                            } else {
                                const cat = categoryMap[id];
                                if (
                                    cat &&
                                    cat.name
                                        .toLowerCase()
                                        .indexOf(inputValue.toLowerCase()) >= 0
                                ) {
                                    return true;
                                }
                                return false;
                            }
                        }}
                        value={
                            /* Yeah yeah yeah I don't know how AntD makes
                            the difference with the absence of prop, but
                            there is a difference: the component is
                            controlled. */ undefined
                        }
                        onSelect={(value?: string | number) => {
                            if (value === undefined) {
                                throw new Error(
                                    "Expected value to be of type string or number, got undefined"
                                );
                            }
                            if (this.props.type !== "EDIT") {
                                throw new Error(
                                    "expected type EDIT, got " + this.props.type
                                );
                            }
                            if (tagMap[value]) {
                                this.props.onAddTag(value?.toString());
                            }
                        }}
                    >
                        {tree.map(({ category, tags }) => (
                            <TreeSelect.TreeNode
                                value={category.id}
                                title={category.name}
                                key={category.id}
                                selectable={false}
                            >
                                {tags.map((tag) => (
                                    <TreeSelect.TreeNode
                                        value={tag.id}
                                        title={tag.name}
                                        key={tag.id}
                                    />
                                ))}
                            </TreeSelect.TreeNode>
                        ))}
                    </TreeSelect>
                );
                break;
            case "READ_ONLY":
                rowSelection = {
                    type: "checkbox" as const,
                    selectedRowKeys,
                    getCheckboxProps: (row: TagRow) => {
                        const cbProps = getCheckboxProps(row);
                        cbProps.disabled = true;
                        return cbProps;
                    },
                };
                footer = undefined;
                break;
            default:
                return assertNever(this.props);
        }
        return (
            <div>
                <Table
                    className={theme.table}
                    columns={[
                        {
                            render: (text, row) => (
                                <div>
                                    <div>{row.tag.name}</div>
                                    <div className={theme.category}>
                                        {row.category.name}
                                    </div>
                                </div>
                            ),
                        },
                    ]}
                    dataSource={dataSource}
                    rowKey={(row) => row.tag.id}
                    rowSelection={rowSelection}
                    bordered
                    showHeader={false}
                    pagination={false}
                    footer={footer}
                />
            </div>
        );
    };
}

class WrappedReadOnlyTagsSelector extends React.PureComponent<WrappedReadProps> {
    render = () => {
        const { values, allTagCategories, allTags } = this.props;
        return (
            <RawTagsSelector
                type="READ_ONLY"
                values={values}
                allTagCategories={allTagCategories}
                allTags={allTags}
            />
        );
    };
}

class WrappedEditTagsSelector extends React.PureComponent<WrappedWriteProps> {
    render = () => {
        const { values, allTagCategories, allTags, onAddTag, onRemoveTag } =
            this.props;
        return (
            <RawTagsSelector
                type="EDIT"
                values={values}
                allTagCategories={allTagCategories}
                allTags={allTags}
                onAddTag={onAddTag}
                onRemoveTag={onRemoveTag}
            />
        );
    };
}

class WrappedCreateTagsSelector extends React.PureComponent<WrappedWriteProps> {
    render = () => {
        const { values, allTagCategories, allTags, onAddTag, onRemoveTag } =
            this.props;
        return (
            <RawTagsSelector
                type="EDIT"
                values={values}
                allTagCategories={allTagCategories}
                allTags={allTags}
                onAddTag={onAddTag}
                onRemoveTag={onRemoveTag}
            />
        );
    };
}

export { type TagsSelectorProps, type TagsSelectorValues, TagsSelector };
